import { Injectable } from '@angular/core';
import { LoadChildrenCallback, Route, Router, Routes } from '@angular/router';
import { importLazyModule } from 'src/lazy-modules';
import { AppRoutingModule } from '../app-routing.module';
import { guardMapping } from './guards/guard-mapping';
import { RouteData } from './route-data.model';
import { RoutesDataService } from './routes-data.service';
import { NotificationService } from '../services/notification.service';
import { NavigationService } from '../core/navigation/services/navigation.service';
@Injectable({
	providedIn: 'root'
})
export class RouteConfigService {

	private buildRouteTree(routeData: RouteData[], parentId: number | null = null): RouteData[] {
		const routes = routeData.filter((route) => route.parentId === parentId);

		routes.forEach(
			(route) => {
				route.children = this.buildRouteTree(routeData, route.id);
			}
		);

		return routes;
	}

	public async initializeRoutes(): Promise<RouteData[]> {
		const routeData = await this._routesDataService.getRoutesData();

		const routeTree = this.buildRouteTree(routeData);

		const processedRoutes = this.processRouteData(routeTree);

		const combinedRoutes: Routes = [
			AppRoutingModule.emptyPathRoute,
			...AppRoutingModule.staticRoutes,
			...processedRoutes,
			AppRoutingModule.wildcardRoute
		];

		this._router.resetConfig(combinedRoutes);
		this._navigationService.resetComponentRouteLookup(combinedRoutes);

		return routeData;
	}

	private processRouteData(routeData: RouteData[]): Routes {
		return routeData.map(
			(route) => {
				let children: Routes | undefined;

				if (route.children && route.children.length > 0) {
					children = this.processRouteData(route.children);

					if (children[0].path) {
						children.unshift(
							{
								path: '',
								redirectTo: children[0].path,
								pathMatch: 'full',
							}
						);
					}
				}

				// Group guards by their guard type.
				const guardsString = route.guards;
				const guardPairs = guardsString ? guardsString.split('|') : [];
				const guardsByType: { [key: string]: any[] } = {};

				guardPairs.forEach(
					(pair) => {
						const [guardType, className] = pair.split(':');

						if (!guardsByType[guardType]) {
							guardsByType[guardType] = [];
						}

						guardsByType[guardType].push(guardMapping[className]);
					}
				);

				if (!route.redirectTo && !children && (!route.modulePath || !route.name)) {
					console.error("Module path or name for a Route is missing. Routing may not work as expected.", route.modulePath, route.name);

					this._notificationService.error("Module path or name for a Route is missing. Routing may not work as expected.");
				}

				const loadChildrenCallback: LoadChildrenCallback = async () => {
					const importedModule = await importLazyModule(route.modulePath);

					return importedModule[route.name];
				};

				const routeConfig: Route = {
					title: route.title,
					path: route.path,
					pathMatch: route.pathMatch,
					redirectTo: route.redirectTo === null ? undefined : route.redirectTo,
					outlet: route.outlet === null ? undefined : route.outlet,

					loadChildren: children || route.redirectTo
						? undefined
						: loadChildrenCallback,

					children: children,


					data: {
						id: route.id,
						parentId: route.parentId,
						name: route.name,
					},
				};

				// Dynamically assign guard types to the route.
				for (const [guardType, guards] of Object.entries(guardsByType)) {
					routeConfig[guardType] = guards;
				}

				return routeConfig;
			}
		);
	}

	constructor(
		private _router: Router,
		private _routesDataService: RoutesDataService,
		private _navigationService: NavigationService,
		private _notificationService: NotificationService,
	) { }
}
